import React, { useState, useRef } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { DownloadIcon, FileIcon, ExternalLinkIcon, CodeIcon, XIcon, CopyIcon, CheckIcon, MousePointerClickIcon, PlayIcon } from 'lucide-react';
const SIG_FULL_URL = 'https://cdn.magicpatterns.com/uploads/hMPDiGdLppb8jSob3ULMCv/emailsig.png';
const EMAIL_SIGNATURE_HTML = `<table cellpadding="0" cellspacing="0" border="0" width="800" style="width: 800px; border-collapse: collapse; table-layout: fixed;">
  <tr>
    <td valign="top">
      <a href="https://anita-headwaytuition.zohobookings.eu/#/ht-discoverysessions" target="_blank" rel="noopener noreferrer" style="text-decoration: none; border: 0; display: block;">
        <img src="${SIG_FULL_URL}" alt="Headway Tuition Email Signature" width="800" height="260" style="display: block; border: 0;" />
      </a>
    </td>
  </tr>
</table>`;
export function FinalAssets() {
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  // Selects the rendered preview and copies it as rich content (text/html)
  // so it can be pasted directly into Gmail / Outlook signature settings
  // with all images, layout and styling intact.
  const handleSelectAndCopy = async () => {
    const node = previewRef.current;
    if (!node) return;
    // Visibly select all the preview assets for the user.
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(node);
    selection?.removeAllRanges();
    selection?.addRange(range);
    let success = false;
    // Preferred: write rich HTML + plain text so the paste keeps images/layout.
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard && 'write' in navigator.clipboard) {
        const html = node.innerHTML;
        await navigator.clipboard.write([new ClipboardItem({
          'text/html': new Blob([html], {
            type: 'text/html'
          }),
          'text/plain': new Blob([EMAIL_SIGNATURE_HTML], {
            type: 'text/plain'
          })
        })]);
        success = true;
      }
    } catch (err) {
      success = false;
    }
    // Fallback: copy the current selection (rich) via execCommand.
    if (!success) {
      try {
        success = document.execCommand('copy');
      } catch (err) {
        success = false;
      }
    }
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };
  return <div className="min-h-screen bg-[#d9e2e8] p-2 md:p-4 font-sans selection:bg-accent selection:text-white" data-id="element-310">
      <div className="main-container max-w-[1600px] mx-auto bg-card rounded-[1.5rem] md:rounded-[2rem] border border-black/5 shadow-sm min-h-[calc(100vh-2rem)] flex flex-col" data-id="element-311">
        <Navigation activePath="/final-assets" data-id="element-312" />

        <main className="flex-1 px-4 md:px-16 py-8 md:py-12 space-y-8 md:space-y-12" data-id="element-313">
          <section className="max-w-4xl" data-id="element-314">
            <div className="flex items-center gap-3" data-id="element-315">
              <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-accent" data-id="element-316" />
              <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground" data-id="element-317">
                Production Ready
              </span>
            </div>
            <h1 className="mt-4 font-display text-4xl md:text-7xl tracking-[-0.04em] text-ink leading-tight" data-id="element-318">
              Final Assets
              <span className="text-accent inline-block w-2 md:w-3 h-2 md:h-3 rounded-full ml-2 mb-1" data-id="element-319" />
            </h1>
            <p className="mt-4 text-base leading-[1.6] text-ink/70 font-medium max-w-2xl" data-id="element-320">
              Access and download the approved production files for Headway
              Tuition brand applications.
            </p>
          </section>

          <div className="grid grid-cols-1 gap-6 md:gap-8 max-w-6xl" data-id="element-321">
            {/* Image Library */}
            <section className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 border border-black/5 shadow-sm flex flex-col md:flex-row gap-8 items-stretch" data-id="element-322">
              <div className="flex-1 space-y-6 flex flex-col justify-between" data-id="element-323">
                <div className="space-y-4" data-id="element-324">
                  <div className="bg-[#d2ff46] w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm drop-shadow-sm" data-id="element-325">
                    <FileIcon size={20} data-id="element-326" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-ink" data-id="element-327">
                    Image Library
                  </h2>
                  <p className="text-ink/60 text-sm leading-relaxed" data-id="element-328">
                    Approved brand photography and visual assets.
                    High-resolution files optimized for both digital and print
                    applications.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-329">
                    <div className="px-3 py-1.5 rounded-lg bg-[#d2ff46] border border-[#d2ff46]/20 shadow-sm" data-id="element-330">
                      <p className="text-[9px] font-bold text-black uppercase tracking-wider" data-id="element-331">
                        Usage
                      </p>
                      <p className="text-[11px] font-bold text-black" data-id="element-332">
                        CMYK (Print), RGB (Digital)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3" data-id="element-333">
                  <a href="https://drive.google.com/file/d/1T8425TQv8J-X4zsbg0xvkIU7epHU1wNu/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-between p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-sm hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/20" data-id="element-334">
                    <span className="flex items-center gap-3" data-id="element-335">
                      <DownloadIcon size={18} className="text-black" data-id="element-336" />
                      Download Image Assets
                    </span>
                    <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-337" />
                  </a>
                </div>
              </div>

              <div className="flex-1 rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] shadow-inner min-h-[200px] grid grid-cols-2 gap-2 p-2" data-id="element-338">
                <img src="https://cdn.magicpatterns.com/uploads/8qaVzhbBqbgDxXRhc5QHSB/image-preview.png" alt="Brand Image 1" className="w-full h-full object-cover rounded-lg" data-id="element-339" />
                <img src="https://cdn.magicpatterns.com/uploads/9yUoPf4537dLcbGSjQ7Tja/image-preview-2.png" alt="Brand Image 2" className="w-full h-full object-cover rounded-lg" data-id="element-340" />
              </div>
            </section>

            {/* Final Logo Assets */}
            <section className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 border border-black/5 shadow-sm flex flex-col md:flex-row gap-8 items-stretch" data-id="element-341">
              <div className="flex-1 space-y-6 flex flex-col justify-between" data-id="element-342">
                <div className="space-y-4" data-id="element-343">
                  <div className="bg-primary/10 w-10 h-10 rounded-xl flex items-center justify-center text-primary" data-id="element-344">
                    <FileIcon size={20} data-id="element-345" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-ink" data-id="element-346">
                    Final Logo Assets
                  </h2>
                  <p className="text-ink/60 text-sm leading-relaxed" data-id="element-347">
                    Complete set of approved logo marks in all required formats
                    (SVG, PNG, PDF, JPG, AI). Includes primary, secondary, and
                    icon versions.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-348">
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-349">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-350">
                        Formats
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-351">
                        SVG, PNG, PDF, AI
                      </p>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-352">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-353">
                        Colours
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-354">
                        RGB, CMYK, HEX
                      </p>
                    </div>
                  </div>
                </div>

                <a href="https://drive.google.com/file/d/1XCXHAEuZ2G3i7dAurmQBp0P-7cJiYUFN/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between w-full p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-sm hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/20" data-id="element-355">
                  <span className="flex items-center gap-3" data-id="element-356">
                    <DownloadIcon size={18} className="text-black" data-id="element-357" />
                    Download Logo Package
                  </span>
                  <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-358" />
                </a>
              </div>

              <div className="flex-1 rounded-xl overflow-hidden border border-black/5 bg-white p-6 flex items-center justify-center shadow-inner min-h-[200px]" data-id="element-359">
                <img src="https://cdn.magicpatterns.com/uploads/uLDsJSrq12khJAwsVZFSrj/headway-logo-rgb-black.png" alt="Headway Logo Thumbnail" className="max-w-[240px] w-full h-auto block" data-id="element-360" />
              </div>
            </section>

            {/* Logo Reveal Animation */}
            <section className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 border border-black/5 shadow-sm flex flex-col md:flex-row gap-8 items-stretch" data-id="element-361">
              <div className="flex-1 space-y-6 flex flex-col justify-between" data-id="element-362">
                <div className="space-y-4" data-id="element-363">
                  <div className="bg-accent/10 w-10 h-10 rounded-xl flex items-center justify-center text-accent" data-id="element-364">
                    <PlayIcon size={20} data-id="element-365" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-ink" data-id="element-366">
                    Logo Reveal Animation
                  </h2>
                  <p className="text-ink/60 text-sm leading-relaxed" data-id="element-367">
                    Final production file for the logo reveal animation.
                    Optimized for digital use across social media and video
                    content.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-368">
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-369">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-370">
                        Format
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-371">MP4</p>
                    </div>
                  </div>
                </div>

                <a href="https://drive.google.com/file/d/1eBc2kKHTTPPlmRDyObJXdE6W1AdS61ct/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between w-full p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-sm hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/20" data-id="element-372">
                  <span className="flex items-center gap-3" data-id="element-373">
                    <DownloadIcon size={18} className="text-black" data-id="element-374" />
                    Download Animation
                  </span>
                  <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-375" />
                </a>
              </div>

              <div className="flex-1 rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] shadow-inner min-h-[200px] flex items-center justify-center p-4" data-id="element-376">
                <video src="https://cdn.magicpatterns.com/uploads/jRk27Tp6NuEeymKPKoTWLS/headway-logo-animation-text-1.mp4" autoPlay loop muted playsInline className="max-h-[240px] w-auto object-contain rounded-lg shadow-sm" data-id="element-377" />
              </div>
            </section>

            {/* A1 and A0 Posters */}
            <section className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 border border-black/5 shadow-sm flex flex-col md:flex-row gap-8 items-stretch" data-id="element-378">
              <div className="flex-1 space-y-6 flex flex-col justify-between" data-id="element-379">
                <div className="space-y-4" data-id="element-380">
                  <div className="bg-accent/10 w-10 h-10 rounded-xl flex items-center justify-center text-accent" data-id="element-381">
                    <FileIcon size={20} data-id="element-382" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-ink" data-id="element-383">
                    A1 and A0 Posters
                  </h2>
                  <p className="text-ink/60 text-sm leading-relaxed" data-id="element-384">
                    Final production files for the brand posters. High-impact
                    visuals designed for large format display in A1 and A0
                    sizes.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-385">
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-386">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-387">
                        Format
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-388">
                        PDF (Print)
                      </p>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-389">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-390">
                        Sizes
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-391">
                        A1, A0
                      </p>
                    </div>
                  </div>
                </div>

                <a href="https://drive.google.com/file/d/1PeFRphXp2MSanchfX65CRVxMSXygJr9K/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between w-full p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-sm hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/20" data-id="element-392">
                  <span className="flex items-center gap-3" data-id="element-393">
                    <DownloadIcon size={18} className="text-black" data-id="element-394" />
                    Download Final Files
                  </span>
                  <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-395" />
                </a>
              </div>

              <div className="flex-1 grid grid-cols-2 gap-4" data-id="element-396">
                <div className="rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] flex items-center justify-center p-2 shadow-inner" data-id="element-397">
                  <img src="https://cdn.magicpatterns.com/uploads/gb7hUfPWg3Brxa5PszwbSF/a1-poster1.png" alt="A1 Poster 1 Preview" className="max-h-[220px] w-auto object-contain shadow-sm" data-id="element-398" />
                </div>
                <div className="rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] flex items-center justify-center p-2 shadow-inner" data-id="element-399">
                  <img src="https://cdn.magicpatterns.com/uploads/5LH62iupnFmmpeh1imFJaY/a1-poster2.png" alt="A1 Poster 2 Preview" className="max-h-[220px] w-auto object-contain shadow-sm" data-id="element-400" />
                </div>
              </div>
            </section>

            {/* Roll Up Banners */}
            <section className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 border border-black/5 shadow-sm flex flex-col md:flex-row gap-8 items-stretch" data-id="element-401">
              <div className="flex-1 space-y-6 flex flex-col justify-between" data-id="element-402">
                <div className="space-y-4" data-id="element-403">
                  <div className="bg-accent/10 w-10 h-10 rounded-xl flex items-center justify-center text-accent" data-id="element-404">
                    <FileIcon size={20} data-id="element-405" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-ink" data-id="element-406">
                    Roll Up Banners
                  </h2>
                  <p className="text-ink/60 text-sm leading-relaxed" data-id="element-407">
                    Final high-impact banners designed for events and tutors'
                    locations. Print-ready with correct bleeds and colour
                    profiles.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-408">
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-409">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-410">
                        Format
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-411">
                        PDF (Print)
                      </p>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-412">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-413">
                        Size
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-414">
                        850mm x 2000mm
                      </p>
                    </div>
                  </div>
                </div>

                <a href="https://drive.google.com/file/d/1eaLIHtYCkDkIRdz9oIeYSrwffL8m5TgX/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between w-full p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-sm hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/20" data-id="element-415">
                  <span className="flex items-center gap-3" data-id="element-416">
                    <DownloadIcon size={18} className="text-black" data-id="element-417" />
                    Download Final Files
                  </span>
                  <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-418" />
                </a>
              </div>

              <div className="flex-1 grid grid-cols-2 gap-4" data-id="element-419">
                <div className="rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] flex items-center justify-center p-4 shadow-inner" data-id="element-420">
                  <img src="https://cdn.magicpatterns.com/uploads/e4owmohDMeAmWYmpUnCV1D/4A_Final_Rollup.png" alt="Banner 4A Preview" className="max-h-[220px] w-auto object-contain" data-id="element-421" />
                </div>
                <div className="rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] flex items-center justify-center p-4 shadow-inner" data-id="element-422">
                  <img src="https://cdn.magicpatterns.com/uploads/3UhPhFGxHQnRSYSrxi1gJc/4D_Final_Rollup.png" alt="Banner 4D Preview" className="max-h-[220px] w-auto object-contain" data-id="element-423" />
                </div>
              </div>
            </section>

            {/* Booklet Section */}
            <section className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 border border-black/5 shadow-sm flex flex-col md:flex-row gap-8 items-stretch" data-id="element-424">
              <div className="flex-1 space-y-6 flex flex-col justify-between" data-id="element-425">
                <div className="space-y-4" data-id="element-426">
                  <div className="bg-[#9E00E8]/10 w-10 h-10 rounded-xl flex items-center justify-center text-[#9E00E8]" data-id="element-427">
                    <FileIcon size={20} data-id="element-428" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-ink" data-id="element-429">
                    Booklet
                  </h2>
                  <p className="text-ink/60 text-sm leading-relaxed" data-id="element-430">
                    Final production files for the 28-page brand booklet.
                    Includes print-ready PDF with correct bleeds and margins.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-431">
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-432">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-433">
                        Format
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-434">
                        PDF (Print)
                      </p>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-435">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-436">
                        Pages
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-437">
                        28 Pages
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-438">
                  <a href="https://drive.google.com/file/d/1pjgFtH2LL0C_dvXJU1EeEHddCn3gA5sy/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between w-full p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-sm hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/20" data-id="element-439">
                    <span className="flex items-center gap-3" data-id="element-440">
                      <DownloadIcon size={18} className="text-black" data-id="element-441" />
                      Download Final Files
                    </span>
                    <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-442" />
                  </a>

                  <a href="https://drive.google.com/drive/folders/1_cNs1-xe-C4gjAuLKbJs3Y8EfqReXIpM?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between w-full p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-xs hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/10" data-id="element-443">
                    <div className="flex-1 flex flex-col text-left" data-id="element-444">
                      <span className="flex items-center gap-2" data-id="element-445">
                        <DownloadIcon size={16} className="text-black" data-id="element-446" />
                        Download Digital (RGB) version of the Headway booklet
                      </span>
                      <span className="text-[9px] font-bold mt-1 ml-6 leading-tight" data-id="element-447">
                        50MB and Compressed 2MB files can be downloaded from
                        here
                      </span>
                    </div>
                    <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity shrink-0" data-id="element-448" />
                  </a>
                </div>
              </div>

              <div className="flex-1 rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] shadow-inner min-h-[200px]" data-id="element-449">
                <img src="https://cdn.magicpatterns.com/uploads/tXiudMLpBfkhR28ktaBjSQ/Headway_Tuition_Mockup.png" alt="Booklet Mockup" className="w-full h-full object-cover block" data-id="element-450" />
              </div>
            </section>

            {/* LinkedIn Banner */}
            <section className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 border border-black/5 shadow-sm flex flex-col md:flex-row gap-8 items-stretch" data-id="element-451">
              <div className="flex-1 space-y-6 flex flex-col justify-between" data-id="element-452">
                <div className="space-y-4" data-id="element-453">
                  <div className="bg-[#0077B5]/10 w-10 h-10 rounded-xl flex items-center justify-center text-[#0077B5]" data-id="element-454">
                    <FileIcon size={20} data-id="element-455" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-ink" data-id="element-456">
                    Linkedin Banner
                  </h2>
                  <p className="text-ink/60 text-sm leading-relaxed" data-id="element-457">
                    Final production files for the LinkedIn company and personal
                    banners. Optimized for high-resolution display on all
                    devices.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-458">
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-459">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-460">
                        Format
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-461">
                        PNG, PDF
                      </p>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-462">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-463">
                        Size
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-464">
                        1584px x 396px
                      </p>
                    </div>
                  </div>
                </div>

                <a href="https://drive.google.com/file/d/1v7zjy1i14EFLlJh57o-_a27EOdTScE5O/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between w-full p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-sm hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/20" data-id="element-465">
                  <span className="flex items-center gap-3" data-id="element-466">
                    <DownloadIcon size={18} className="text-black" data-id="element-467" />
                    Download Final Files
                  </span>
                  <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-468" />
                </a>
              </div>

              <div className="flex-1 rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] shadow-inner min-h-[200px] flex items-center justify-center p-4" data-id="element-469">
                <img src="https://cdn.magicpatterns.com/uploads/wMpwgGvMtjqoq6pPEFMHqa/headway-linkedin-company-banner.png" alt="LinkedIn Banner Preview" className="w-full h-auto block shadow-sm" data-id="element-470" />
              </div>
            </section>

            {/* Email Signature */}
            <section className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 border border-black/5 shadow-sm flex flex-col md:flex-row gap-8 items-stretch" data-id="element-471">
              <div className="flex-1 space-y-6 flex flex-col justify-between" data-id="element-472">
                <div className="space-y-4" data-id="element-473">
                  <div className="bg-accent/10 w-10 h-10 rounded-xl flex items-center justify-center text-accent" data-id="element-474">
                    <FileIcon size={20} data-id="element-475" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-ink" data-id="element-476">
                    Email Signature
                  </h2>
                  <p className="text-ink/60 text-sm leading-relaxed" data-id="element-477">
                    Final production files for the branded email signature.
                    Includes the HTML template and assets ready to install
                    across the team.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-478">
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-479">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-480">
                        Format
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-481">
                        HTML, PNG
                      </p>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-black/5" data-id="element-482">
                      <p className="text-[9px] font-bold text-ink/40 uppercase tracking-wider" data-id="element-483">
                        Type
                      </p>
                      <p className="text-[11px] font-medium text-ink/70" data-id="element-484">
                        Email Ready
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3" data-id="element-485">
                  <a href="https://drive.google.com/file/d/1GXLx3K2_HUDK1ozTZiN-yEwx3jj1vfZX/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-between p-4 bg-[#d2ff46] text-black rounded-xl font-bold text-sm hover:bg-[#d2ff46]/90 transition-all group shadow-lg shadow-[#d2ff46]/20" data-id="element-486">
                    <span className="flex items-center gap-3" data-id="element-487">
                      <DownloadIcon size={18} className="text-black" data-id="element-488" />
                      Download Final Files
                    </span>
                    <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-489" />
                  </a>
                  <button type="button" onClick={() => setShowSignatureModal(true)} className="flex-1 flex items-center justify-between p-4 bg-white border-2 border-primary text-primary rounded-xl font-bold text-sm hover:bg-primary hover:text-white transition-all group shadow-lg shadow-primary/5" data-id="element-490">
                    <span className="flex items-center gap-3" data-id="element-491">
                      <CodeIcon size={18} data-id="element-492" />
                      View HTML Email Signature
                    </span>
                    <ExternalLinkIcon size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-493" />
                  </button>
                </div>
              </div>

              <div className="flex-1 rounded-xl overflow-hidden border border-black/5 bg-[#f8f9fa] shadow-inner min-h-[200px] flex items-center justify-center p-4" data-id="element-494">
                <img src="https://cdn.magicpatterns.com/uploads/khfwdpkTA9AyCmYdQL9bus/headway-email-signature-2.png" alt="Email Signature Preview" className="w-full h-auto block shadow-sm" data-id="element-495" />
              </div>
            </section>
          </div>
        </main>
        <Footer data-id="element-496" />
      </div>

      {showSignatureModal && <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-2 md:p-6 bg-black/50 backdrop-blur-sm overflow-y-auto" onClick={() => setShowSignatureModal(false)} role="dialog" aria-modal="true" aria-label="HTML Email Signature" data-id="element-497">
          <div className="bg-white rounded-[1.25rem] md:rounded-[2rem] w-full max-w-none my-2 md:my-0 shadow-2xl border border-black/5 overflow-hidden flex flex-col max-h-[96vh]" onClick={e => e.stopPropagation()} data-id="element-498">
            <div className="flex items-center justify-between gap-4 px-5 md:px-10 py-5 border-b border-black/5 shrink-0" data-id="element-499">
              <div className="flex items-center gap-3" data-id="element-500">
                <div className="bg-primary/10 w-9 h-9 rounded-xl flex items-center justify-center text-primary" data-id="element-501">
                  <CodeIcon size={18} data-id="element-502" />
                </div>
                <h3 className="text-base md:text-lg font-bold tracking-tight text-ink" data-id="element-503">
                  HTML Email Signature
                </h3>
              </div>
              <div className="flex items-center gap-2" data-id="element-504">
                <button type="button" onClick={handleSelectAndCopy} className="flex items-center gap-2 px-3 md:px-5 py-2.5 bg-primary text-white rounded-lg font-bold text-xs md:text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/10" data-id="element-505">
                  {copied ? <>
                      <CheckIcon size={15} data-id="element-506" /> Selected &amp; copied
                    </> : <>
                      <MousePointerClickIcon size={15} data-id="element-507" /> Select all &amp; copy
                    </>}
                </button>
                <button type="button" onClick={() => setShowSignatureModal(false)} className="w-9 h-9 rounded-lg flex items-center justify-center text-ink/50 hover:bg-black/5 hover:text-ink transition-all" aria-label="Close" data-id="element-508">
                  <XIcon size={18} data-id="element-509" />
                </button>
              </div>
            </div>

            <div className="p-5 md:p-10 space-y-6 overflow-y-auto" data-id="element-510">
              <div className="rounded-xl border border-primary/15 bg-primary/5 p-4 flex items-start gap-3" data-id="element-511">
                <MousePointerClickIcon size={18} className="text-primary shrink-0 mt-0.5" data-id="element-512" />
                <p className="text-[12px] md:text-sm text-ink/70 leading-relaxed" data-id="element-513">
                  Click{' '}
                  <span className="font-bold text-ink" data-id="element-514">
                    Select all &amp; copy
                  </span>{' '}
                  to select the full signature below and copy it with all images
                  and styling. Then paste it straight into your Gmail or Outlook
                  signature settings.
                </p>
              </div>

              <div data-id="element-515">
                <p className="text-[10px] font-bold text-ink/40 uppercase tracking-widest mb-3" data-id="element-516">
                  Live Preview — select all and copy this
                </p>
                <div className="rounded-xl border border-black/5 bg-white p-4 md:p-8 overflow-x-auto shadow-inner" data-id="element-517">
                  <div ref={previewRef} className="min-w-[300px]" dangerouslySetInnerHTML={{
                __html: EMAIL_SIGNATURE_HTML
              }} data-id="element-518" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-519">
                <div className="rounded-xl border border-black/5 bg-gray-50 p-4" data-id="element-520">
                  <p className="text-xs font-bold text-ink mb-2" data-id="element-521">
                    Install in Gmail
                  </p>
                  <ol className="text-[11px] text-ink/60 leading-relaxed list-decimal pl-4 space-y-1" data-id="element-522">
                    <li data-id="element-523">Settings → See all settings → General.</li>
                    <li data-id="element-524">Scroll to "Signature" and create a new one.</li>
                    <li data-id="element-525">Paste the rendered signature into the editor.</li>
                    <li data-id="element-526">Save changes at the bottom of the page.</li>
                  </ol>
                </div>
                <div className="rounded-xl border border-black/5 bg-gray-50 p-4" data-id="element-527">
                  <p className="text-xs font-bold text-ink mb-2" data-id="element-528">
                    Install in Outlook
                  </p>
                  <ol className="text-[11px] text-ink/60 leading-relaxed list-decimal pl-4 space-y-1" data-id="element-529">
                    <li data-id="element-530">Settings → Mail → Compose and reply.</li>
                    <li data-id="element-531">Create a new signature.</li>
                    <li data-id="element-532">Paste the rendered signature in.</li>
                    <li data-id="element-533">Set as default and save.</li>
                  </ol>
                </div>
              </div>

              <div className="rounded-xl border border-black/5 bg-accent/5 p-4" data-id="element-534">
                <p className="text-xs font-bold text-ink mb-1" data-id="element-535">
                  Compatibility notes
                </p>
                <p className="text-[11px] text-ink/60 leading-relaxed" data-id="element-536">
                  This signature is a single high-resolution image linked to
                  your Zoho booking page. This ensures 100% visual consistency
                  across all email clients (Gmail, Outlook, Apple Mail, etc.)
                  and mobile devices. The entire signature acts as a clickable
                  link to your discovery session booking page.
                </p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}